import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "@repo/blacksmith-ui/cn";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@repo/blacksmith-ui/hover-card";
import { SidebarMenuButton, SidebarMenuItem, useSidebar } from "../ui/sidebar";
import { RoutePrefetchLink } from "../ui/route-prefetch-link";
export type NavSubItem = {
  name: string;
  href: string;
  current: boolean;
  icon: React.ReactNode;
  onMouseEnter?: () => void;
};

/**
 * Contextual-sidebar row that opens its sub-pages in a hover card to the
 * right (a tap toggle on mobile), e.g. Run History → Jobs / Workflows. The
 * row reads active whenever one of its sub-items is.
 */
// Opening one row's card closes its siblings; the trigger stops pointerdown
// propagation, so Radix's outside-click dismissal can't do it on mobile.
const OPEN_EVENT = "sidebar-hover-card-open";
export function SidebarHoverCardMenuItem(t0) {
  const $ = _c(64);
  const {
    name,
    icon,
    subItems,
    prefetchOnIntent,
    buttonClassName
  } = t0;
  const {
    isMobile
  } = useSidebar();
  const [open, setOpenState] = useState(false);
  const id = useId();
  const focusFirstLink = useRef(false);
  const [highlighted, setHighlighted] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = next => {
      setOpenState(next);
      if (!next) {
        setHighlighted(null);
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const setOpen = t1;
  const triggerRef = useRef(null);
  const contentRef = useRef(null);
  const isOwnNode = _temp;
  let t2;
  let t3;
  if ($[1] !== id || $[2] !== open) {
    t2 = () => {
      if (!open) {
        return;
      }
      window.dispatchEvent(new CustomEvent(OPEN_EVENT, {
        detail: id
      }));
      const closeIfOther = e => {
        if ((e as CustomEvent<string>).detail !== id) {
          setOpen(false);
        }
      };
      window.addEventListener(OPEN_EVENT, closeIfOther);
      return () => window.removeEventListener(OPEN_EVENT, closeIfOther);
    };
    t3 = [open, id];
    $[1] = id;
    $[2] = open;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = node => {
      if (!node || !focusFirstLink.current) {
        return;
      }
      focusFirstLink.current = false;
      requestAnimationFrame(() => node.querySelector("a")?.focus());
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const focusFirstLinkOnMount = t4;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = e_0 => {
      const forward = e_0.key === "ArrowDown" || e_0.key === "Tab" && !e_0.shiftKey;
      const back = e_0.key === "ArrowUp" || e_0.key === "Tab" && e_0.shiftKey;
      if (!forward && !back) {
        return;
      }
      const links = Array.from(e_0.currentTarget.querySelectorAll("a"));
      const index = links.findIndex(_temp2);
      if (index === -1) {
        return;
      }
      const next_0 = links[index + (forward ? 1 : -1)];
      if (next_0) {
        e_0.preventDefault();
        next_0.focus();
        return;
      }
      if (e_0.key !== "Tab") {
        e_0.preventDefault();
        return;
      }
      setOpen(false);
      triggerRef.current?.focus();
      if (!(e_0.key === "Tab" && forward)) {
        e_0.preventDefault();
      }
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const handleCardKeyDown = t5;
  let t6;
  if ($[7] !== isMobile) {
    t6 = next_1 => {
      if (isMobile && !next_1) {
        setOpen(false);
      }
    };
    $[7] = isMobile;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== subItems) {
    t7 = subItems.some(_temp3);
    $[9] = subItems;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== buttonClassName) {
    t8 = cn("h-8 rounded-md p-1.5 group-data-[state=collapsed]:p-0 group-data-[state=collapsed]:!w-8 group-data-[state=collapsed]:!h-8 group-data-[state=collapsed]:!max-w-8 group-data-[state=collapsed]:!min-w-8 group-data-[collapsible=icon]:!w-8 group-data-[collapsible=icon]:!h-8 group-data-[collapsible=icon]:!max-w-8 group-data-[collapsible=icon]:!min-w-8 group-data-[state=collapsed]:justify-center group-data-[state=collapsed]:mx-auto type-body-s-regular text-neutral-800 hover:!bg-black/[0.04] hover:text-neutral-950 data-[hovered=true]:!bg-black/[0.04] data-[hovered=true]:text-neutral-950 data-[active=true]:!bg-[#0000000f] data-[active=true]:text-neutral-950 [&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900 data-[hovered=true]:[&_svg]:text-neutral-900 data-[active=true]:[&_svg]:text-neutral-900 dark:text-neutral-300 dark:hover:!bg-white/[0.04] dark:hover:text-white dark:data-[hovered=true]:!bg-white/[0.04] dark:data-[hovered=true]:text-white dark:data-[active=true]:!bg-white/6 dark:data-[active=true]:text-white dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white dark:data-[hovered=true]:[&_svg]:text-white dark:data-[active=true]:[&_svg]:text-white data-[hovered=true]:focus-visible:!border-transparent dark:data-[hovered=true]:focus-visible:!border-transparent cursor-pointer", buttonClassName);
    $[11] = buttonClassName;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t10;
  let t11;
  let t9;
  if ($[13] !== isMobile) {
    t9 = () => {
      if (isMobile) {
        return;
      }
      setOpen(true);
    };
    t10 = e_1 => {
      if (isMobile) {
        return;
      }
      if (isOwnNode(e_1.relatedTarget, contentRef)) {
        return;
      }
      setOpen(false);
    };
    t11 = e_2 => {
      e_2.stopPropagation();
      if (isMobile) {
        return;
      }
      setOpen(true);
    };
    $[13] = isMobile;
    $[14] = t10;
    $[15] = t11;
    $[16] = t9;
  } else {
    t10 = $[14];
    t11 = $[15];
    t9 = $[16];
  }
  let t12;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = e_3 => {
      if (e_3.key === "Escape") {
        setOpen(false);
      }
    };
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== isMobile || $[19] !== open) {
    t13 = e_4 => {
      if (!isMobile && e_4.detail === 0) {
        e_4.preventDefault();
        focusFirstLink.current = !open;
        setOpen(!open);
        return;
      }
      if (!isMobile) {
        return;
      }
      e_4.preventDefault();
      e_4.stopPropagation();
      setOpen(!open);
    };
    $[18] = isMobile;
    $[19] = open;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== icon) {
    t14 = <div className="relative">{icon}</div>;
    $[21] = icon;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== name) {
    t15 = <span className="whitespace-nowrap group-data-[state=collapsed]:hidden">{name}</span>;
    $[23] = name;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <ChevronRight className="ml-auto size-[18px] text-neutral-500 dark:text-neutral-400 group-data-[state=collapsed]:hidden" />;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== name || $[27] !== open || $[28] !== t10 || $[29] !== t11 || $[30] !== t13 || $[31] !== t14 || $[32] !== t15 || $[33] !== t7 || $[34] !== t8 || $[35] !== t9) {
    t17 = <HoverCardTrigger asChild={true}><SidebarMenuButton ref={triggerRef} aria-label={name} aria-expanded={open} aria-haspopup="menu" isActive={t7} data-hovered={open} className={t8} onPointerEnter={t9} onPointerLeave={t10} onPointerDownCapture={t11} onKeyDown={t12} onClick={t13}>{t14}{t15}{t16}</SidebarMenuButton></HoverCardTrigger>;
    $[26] = name;
    $[27] = open;
    $[28] = t10;
    $[29] = t11;
    $[30] = t13;
    $[31] = t14;
    $[32] = t15;
    $[33] = t7;
    $[34] = t8;
    $[35] = t9;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  let t19;
  let t20;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => setOpen(false);
    t19 = () => setHighlighted(null);
    t20 = () => {
      setOpen(false);
      triggerRef.current?.focus();
    };
    $[37] = t18;
    $[38] = t19;
    $[39] = t20;
  } else {
    t18 = $[37];
    t19 = $[38];
    t20 = $[39];
  }
  const t21 = isMobile ? "bottom" : "right";
  const t22 = !isMobile && "-translate-y-[5px]";
  let t23;
  if ($[40] !== t22) {
    t23 = cn("w-48 p-1 transition-none", t22);
    $[40] = t22;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  let t24;
  if ($[42] !== isMobile) {
    t24 = () => {
      if (isMobile) {
        return;
      }
      setOpen(true);
    };
    $[42] = isMobile;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  let t25;
  if ($[44] !== isMobile) {
    t25 = e_5 => {
      if (isMobile) {
        return;
      }
      if (isOwnNode(e_5.relatedTarget, triggerRef)) {
        return;
      }
      setOpen(false);
    };
    $[44] = isMobile;
    $[45] = t25;
  } else {
    t25 = $[45];
  }
  let t26;
  if ($[46] !== highlighted || $[47] !== isMobile || $[48] !== prefetchOnIntent || $[49] !== subItems) {
    t26 = subItems.map(subItem_0 => <RoutePrefetchLink key={subItem_0.name} href={subItem_0.href} prefetchOnIntent={prefetchOnIntent} onMouseEnter={subItem_0.onMouseEnter} data-highlighted={highlighted === subItem_0.name} onPointerEnter={e_6 => {
      if (e_6.pointerType === "touch") {
        return;
      }
      if (contentRef.current?.contains(document.activeElement)) {
        e_6.currentTarget.focus({
          preventScroll: true
        });
      } else {
        setHighlighted(subItem_0.name);
      }
    }} onPointerLeave={() => setHighlighted(null)} onClick={() => {
      if (isMobile) {
        setOpen(false);
      }
    }} className={cn("flex items-center gap-2 rounded-md px-2 py-1.5 type-body-s-regular cursor-pointer transition-none outline-none", "text-neutral-800 [&_svg]:text-neutral-600 dark:text-neutral-300 dark:[&_svg]:text-neutral-400", subItem_0.current ? "bg-[#0000000f] text-neutral-950 [&_svg]:text-neutral-900 dark:bg-white/6 dark:text-white dark:[&_svg]:text-white focus:bg-black/[0.08] data-[highlighted=true]:bg-black/[0.08] dark:focus:bg-white/10 dark:data-[highlighted=true]:bg-white/10" : "focus:bg-neutral-50 focus:text-neutral-950 focus:[&_svg]:text-neutral-900 data-[highlighted=true]:bg-neutral-50 data-[highlighted=true]:text-neutral-950 data-[highlighted=true]:[&_svg]:text-neutral-900 dark:focus:bg-[#444545] dark:focus:text-white dark:focus:[&_svg]:text-white dark:data-[highlighted=true]:bg-[#444545] dark:data-[highlighted=true]:text-white dark:data-[highlighted=true]:[&_svg]:text-white")}>{subItem_0.icon}<span>{subItem_0.name}</span></RoutePrefetchLink>);
    $[46] = highlighted;
    $[47] = isMobile;
    $[48] = prefetchOnIntent;
    $[49] = subItems;
    $[50] = t26;
  } else {
    t26 = $[50];
  }
  let t27;
  if ($[51] !== t26) {
    t27 = <div ref={focusFirstLinkOnMount} className="flex flex-col gap-0.5">{t26}</div>;
    $[51] = t26;
    $[52] = t27;
  } else {
    t27 = $[52];
  }
  let t28;
  if ($[53] !== t21 || $[54] !== t23 || $[55] !== t24 || $[56] !== t25 || $[57] !== t27) {
    t28 = <HoverCardContent ref={contentRef} onPointerDownOutside={t18} onFocus={t19} onEscapeKeyDown={t20} side={t21} align="start" sideOffset={8} className={t23} onPointerEnter={t24} onKeyDown={handleCardKeyDown} onPointerLeave={t25}>{t27}</HoverCardContent>;
    $[53] = t21;
    $[54] = t23;
    $[55] = t24;
    $[56] = t25;
    $[57] = t27;
    $[58] = t28;
  } else {
    t28 = $[58];
  }
  let t29;
  if ($[59] !== open || $[60] !== t17 || $[61] !== t28 || $[62] !== t6) {
    t29 = <SidebarMenuItem className="group-data-[state=collapsed]:flex group-data-[state=collapsed]:justify-center group-data-[state=collapsed]:w-full -mx-1"><HoverCard open={open} openDelay={0} closeDelay={0} onOpenChange={t6}>{t17}{t28}</HoverCard></SidebarMenuItem>;
    $[59] = open;
    $[60] = t17;
    $[61] = t28;
    $[62] = t6;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  return t29;
}
function _temp3(subItem) {
  return subItem.current;
}
function _temp2(link) {
  return link === document.activeElement;
}
function _temp(target, ref) {
  return target instanceof Node && !!ref.current?.contains(target);
}